import { useCallback, useRef, useState } from "react";
import { reviewBeforeSend } from "screens/SignRequest/SignRequestService";
import { apiStatus, getApiStatus } from "utils/apiStatus";
import { checkStatus } from "utils/service";
import { normalisePresendReview } from "./PresendReviewHelper";
import {
  PresendReviewRequest,
  PresendReviewResponse,
  PresendReviewState,
} from "./PresendReviewTypes";

/** The countdown when a 429 carries no `retryAfter` of its own. */
const DEFAULT_RETRY_AFTER_SECONDS = 10;

/**
 * State for the pre-send review popup. Nothing here can hold the sender up: every state the review lands
 * in -- including a failed one -- leaves them a way on.
 */
export const usePresendReview = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<PresendReviewState>({ phase: "loading" });
  const [fileNames, setFileNames] = useState<string[]>([]);
  // What the sender dismissed, each as `dismissalKey` (document + finding). Kept for the whole sign-request
  // flow, so a finding dismissed once stays dismissed when the review runs again after a trip back to the
  // editor (ids are stable).
  const [dismissed, setDismissed] = useState<string[]>([]);
  // Set by a 401: the session state offers no way on but Cancel, so after it the review is skipped for
  // the rest of this flow rather than shown again on every Next.
  const [authFailed, setAuthFailed] = useState(false);
  // Each run gets a number; an answer whose run is no longer the latest is dropped.
  const runRef = useRef(0);
  const lastRequestRef = useRef<{
    envelopeId: string;
    payload: PresendReviewRequest;
  } | null>(null);
  // The last answer and what it answered: an unchanged request is shown again, not reviewed again.
  const lastResultRef = useRef<{
    key: string;
    review: PresendReviewResponse;
  } | null>(null);

  const run = useCallback(
    async (envelopeId: string, payload: PresendReviewRequest) => {
      const current = (runRef.current += 1);
      const key = `${envelopeId}:${JSON.stringify(payload)}`;
      lastRequestRef.current = { envelopeId, payload };
      setState({ phase: "loading" });
      try {
        const review = normalisePresendReview(
          await reviewBeforeSend(envelopeId, payload),
        );
        if (current !== runRef.current) return;
        if (review) {
          lastResultRef.current = { key, review };
          setState({ phase: "result", review });
        } else {
          setState({ phase: "unavailable" });
        }
      } catch (error: any) {
        if (current !== runRef.current) return;
        const status = getApiStatus(error);
        if (status === apiStatus.tooManyRequests) {
          const seconds =
            Number(error?.retryAfter) > 0
              ? Number(error.retryAfter)
              : DEFAULT_RETRY_AFTER_SECONDS;
          setState({ phase: "rateLimited", retryAt: Date.now() + seconds * 1000 });
        } else if (status === apiStatus.unauthorized) {
          setAuthFailed(true);
          setState({ phase: "session" });
        } else {
          // 5xx, a timeout, or a 400/404 (an integration bug the sender cannot fix): all read the same.
          setState({ phase: "unavailable" });
        }
      }
    },
    [],
  );

  const open = useCallback(
    (envelopeId: string, payload: PresendReviewRequest) => {
      setFileNames(payload.files);
      setIsOpen(true);
      const last = lastResultRef.current;
      if (last && last.key === `${envelopeId}:${JSON.stringify(payload)}`) {
        runRef.current += 1;
        setState({ phase: "result", review: last.review });
        return;
      }
      run(envelopeId, payload);
    },
    [run],
  );

  const retry = useCallback(() => {
    const last = lastRequestRef.current;
    if (last) run(last.envelopeId, last.payload);
  }, [run]);

  /** Closes the popup; an answer still in flight is dropped when it lands. */
  const close = useCallback(() => {
    runRef.current += 1;
    setIsOpen(false);
  }, []);

  /** `key` is the finding's `dismissalKey`. */
  const dismiss = useCallback(
    (key: string) =>
      setDismissed((prev) => (prev.includes(key) ? prev : [...prev, key])),
    [],
  );

  /**
   * Brings back the dismissals `keys` names -- the review on screen, which is what the footer's count
   * says -- and keeps those of documents this review does not show (one taken out of the envelope, then
   * added back unchanged, keeps its dismissals).
   */
  const restoreDismissed = useCallback(
    (keys: string[]) =>
      setDismissed((prev) => prev.filter((key) => !keys.includes(key))),
    [],
  );

  /**
   * Forgets the last answer. The cache key is the request as sent, which does not see a document's
   * contents: a file deleted and uploaded again under the same name would match it. The caller resets on
   * every change to the documents.
   */
  const resetCache = useCallback(() => {
    lastResultRef.current = null;
  }, []);

  /**
   * The 401 path, through the app's own (`checkStatus`): sign-out, the page remembered, the landing view --
   * and nothing at all inside an embedded widget, which has no Inkfree login to drop. Handed a plain 401:
   * the error the review caught may carry it only as `statusCode`, which `checkStatus` does not read.
   */
  const signInAgain = useCallback(() => {
    runRef.current += 1;
    setIsOpen(false);
    checkStatus({ status: apiStatus.unauthorized });
  }, []);

  return {
    isOpen,
    state,
    fileNames,
    dismissed,
    authFailed,
    open,
    retry,
    close,
    dismiss,
    restoreDismissed,
    resetCache,
    signInAgain,
  };
};
